import { AllowedFileTypesEnum, UploadedFile as UploadedFileType } from "@/api/lctApISchemas";
import useUpload from "@/hooks/useUpload";
import { UploadedFile } from "./components/UploadedFile";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useEffect, useCallback } from "react";
import { UploadDropzone } from "./components/UploadDropzone";

interface UploadQuestionProps {
  allowedFileTypes: AllowedFileTypesEnum[];
  value: string;
  onChange: (value: string) => void;
  userAppId: string;
  questionId: string;
  hasError: boolean;
}

export const UploadQuestion = ({ onChange, userAppId, allowedFileTypes, questionId, hasError }: UploadQuestionProps) => {
  const { uploadedFiles, deleteFile, uploadFile, setFileUploadProgress, uploading, uploadedFilesLoading } = useUpload(
    userAppId,
    questionId,
  );
  const uploadingFile = uploading && uploadedFiles.length === 0;
  const isStaff = useAppSelector((state) => state.user.isStaff);
  const uploadDisabled = uploadingFile || uploadedFilesLoading || isStaff || uploadedFiles.length >= 5;

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      onChange(uploadedFiles.map((f) => f.id).join(","));
    } else {
      onChange("");
    }
  }, [uploadedFiles, onChange]);

  const handleFileUpload = useCallback(
    (file: File) => {
      if (uploadedFiles.length >= 5) {
        return;
      }
      setFileUploadProgress({
        progress: 0,
        File: file,
        source: null,
      });
      uploadFile(file, (progress) => {
        setFileUploadProgress((prev) => (prev ? { ...prev, progress } : prev));
      });
    },
    [setFileUploadProgress, uploadFile, uploadedFiles.length],
  );

  const handleFileDelete = async (file: UploadedFileType) => {
    await deleteFile(file);
  };

  return (
    <div className="max-w-full rounded-md">
      {!isStaff && uploadedFiles.length < 5 && (
        <div className="mb-4 flex h-full w-full items-center justify-center">
          <UploadDropzone
            allowedFileTypes={allowedFileTypes}
            onFilesAdded={handleFileUpload}
            disabled={uploadDisabled}
            hasError={hasError}
          />
        </div>
      )}
      {uploadedFiles.map((file) => (
        <div key={file.id} className="mb-2">
          <UploadedFile file={file} userAppId={userAppId} questionId={questionId} onFileDelete={handleFileDelete} />
        </div>
      ))}
    </div>
  );
};
